import React from "react";
import { C } from "@/lib/foundationTheme";
import { activeProduct } from "@/lib/productBrand";
import { activeMarket } from "@/lib/marketBoot";

/**
 * Foundation brand mark + wordmark lockup — "Title Plan" identity.
 *
 * THE MARK: the triangulation network — kept, unchanged, from the outgoing
 * identity. It is a trig point, its observed stations and the sight lines
 * between them, which is precisely how the Ordnance Survey fixed this country.
 * It was already telling this direction's story before the direction existed,
 * so there was nothing to gain by redrawing it and a great deal to lose: it is
 * the mark on the App Store listing, on nine generated SVGs, and on every
 * agency's browser tab.
 *
 * What the rebrand changed is everything around it — the wordmark moved from a
 * boutique serif to Archivo's expanded cut, and the copper gave way to ink, so
 * the mark now sits *under* a tenant's brand instead of competing with it. The
 * apex node carries the red: the observed point, the one thing on the sheet
 * that is certain, and the single red element that survives down to a 16px
 * favicon.
 *
 * An earlier pass replaced this with a drawn benchmark (a levelling bar over a
 * plumb line). It was rejected — it reads as a letter T. Do not reinstate it.
 */

const RED = C.red;
const INK = C.ink;

/**
 * The symbol alone.
 *
 * One drawing at three optical settings, not one drawing scaled: the sight
 * lines and the inner station are dropped as the mark shrinks, and the stroke
 * thickens, so it stays legible at 16px instead of silting up. Never render
 * the display variant below 64px.
 */
export function FoundationMark({ size = 32, color, tone = "light", className, title, ...rest }) {
  const label = title || activeProduct().name;
  const mono = color != null;
  const stroke = mono ? color : tone === "dark" ? C.paper : INK;
  const apex = mono ? color : tone === "dark" ? C.signal : RED;

  const display = size >= 64;
  const small = size < 24;
  const weight = display ? 1.4 : small ? 4.5 : 2.4;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={label}
      className={className}
      style={{ display: "block", flexShrink: 0 }}
      {...rest}
    >
      <g fill="none" stroke={stroke} strokeWidth={weight} strokeLinejoin="round"
         strokeLinecap="round">
        {activeMarket() === "us" ? (
          /* A statehouse: dome over a portico. Same drawing as the marketing
             site's header, so the product and the site that sells it are not
             wearing different logos. One weight, legible down to 16px. */
          <>
            <path d="M32 13 V20" />
            <path d="M21 31 A11 11 0 0 1 43 31" />
            <path d="M11 31 H53" />
            <path d="M20 31 V53" />
            <path d="M32 31 V53" />
            <path d="M44 31 V53" />
            <path d="M8 53 H56" />
          </>
        ) : small ? (
          <>
            <path d="M32 8 L56 52 H8 Z" />
            <path d="M32 8 V52" />
          </>
        ) : (
          <>
            <path d="M32 7 L57 53 H7 Z" />
            <path d="M19.5 30 H44.5 L32 53 Z" />
            {/* The sight lines survive only at display size — below it they
                close the triangle up into a solid. */}
            {display && <path d="M32 7 V53 M7 53 L44.5 30 M57 53 L19.5 30" />}
          </>
        )}
      </g>
      <g fill={stroke}>
        {small ? (
          <>
            <circle cx="8" cy="52" r="5" /><circle cx="56" cy="52" r="5" />
          </>
        ) : (
          <>
            <circle cx="7" cy="53" r={display ? 2.6 : 3.2} />
            <circle cx="57" cy="53" r={display ? 2.6 : 3.2} />
            <circle cx="19.5" cy="30" r={display ? 2.2 : 2.8} />
            <circle cx="44.5" cy="30" r={display ? 2.2 : 2.8} />
            <circle cx="32" cy="53" r={display ? 2.2 : 2.8} />
            {display && <circle cx="32" cy="38" r="2.6" />}
          </>
        )}
      </g>
      {/* The trig point itself — Foundation's mark is a survey triangle and
          the apex node is the observed point. The statehouse has no apex to
          mark; the same accent lands on the dome's finial instead, which is
          where the eye goes on that drawing. */}
      {activeMarket() === "us" ? (
        <circle cx="32" cy="12" r={small ? 4 : 3.2} fill={apex} />
      ) : (
        <circle cx="32" cy={small ? 8 : 7} r={small ? 6 : display ? 3.4 : 4} fill={apex} />
      )}
    </svg>
  );
}

/** The wordmark on its own: Inter Tight 700, in the name's own case. */
export function FoundationWordmark({ size = 18, color = INK, style, ...rest }) {
  // The product name, not a literal: this file is shared by two products and
  // the wordmark was rendering "Foundation" on Statehouse's legal pages, its
  // 404 and its payment screens.
  const name = activeProduct().name;
  return (
    <span
      style={{
        fontFamily: "'Inter Tight', 'Inter', -apple-system, sans-serif",
        fontWeight: 700,
        fontSize: size * 1.1,
        letterSpacing: "-0.02em",
        color,
        lineHeight: 1,
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {name}
    </span>
  );
}

/**
 * Full lockup.
 *
 * orientation: "horizontal" (site/app header) | "stacked" (hero, footer, print)
 * tone:        "light" (on paper) | "dark" (on ink panels) | "mono" (currentColor)
 * tagline:     optional line under the wordmark
 * product:     optional divider + product name, for CRM/app chrome
 *
 * Prop names and exports are unchanged from the previous identity so the ten
 * files that already mount this keep working.
 */
export function FoundationLogo({
  orientation = "horizontal",
  tone = "light",
  size = 28,
  tagline,
  product,
  className,
  ...rest
}) {
  const stacked = orientation === "stacked";
  const monoColor = tone === "mono" ? "currentColor" : undefined;
  const textColor = tone === "mono" ? "currentColor" : tone === "dark" ? C.paper : INK;
  const softColor = tone === "mono" ? "currentColor" : tone === "dark" ? "rgba(242,239,231,0.6)" : C.secondary;

  // Cap height of Archivo 600 is ~0.72em, so this sizes the wordmark to sit
  // optically level with the mark's overall height rather than measuring equal.
  const wordSize = stacked ? size * 0.52 : size * 0.62;

  return (
    <span
      className={className}
      {...rest}
      style={{
        display: "inline-flex",
        flexDirection: stacked ? "column" : "row",
        alignItems: stacked ? "center" : "center",
        gap: stacked ? size * 0.32 : size * 0.42,
      }}
    >
      <FoundationMark size={size} color={monoColor} tone={tone} />
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: stacked ? "center" : "flex-start",
          gap: wordSize * 0.34,
        }}
      >
        <FoundationWordmark size={wordSize} color={textColor} />
        {tagline && (
          // Hidden below 640px: at this size it is texture, not copy, and in
          // the header flow it used to push the menu button off a 375px screen.
          <span
            className="hidden sm:inline"
            style={{
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: Math.max(9, wordSize * 0.42),
              fontWeight: 500,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: softColor,
              lineHeight: 1.3,
              textAlign: stacked ? "center" : "left",
            }}
          >
            {tagline}
          </span>
        )}
      </span>
      {product && !stacked && (
        <>
          <span
            aria-hidden="true"
            style={{
              width: 1,
              height: size * 0.62,
              background: tone === "dark" ? "rgba(242,239,231,0.24)" : C.hairline,
            }}
          />
          <span
            style={{
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: softColor,
            }}
          >
            {product}
          </span>
        </>
      )}
    </span>
  );
}

/**
 * The endorsement lock — "<Agency>, powered by Foundation".
 *
 * This is a permanent brand surface, not a caption: it appears on every
 * white-label tenant site, every brochure and every market report, which makes
 * it the version of the logo the public actually sees most often. It has to be
 * quiet enough to sit under someone else's identity without competing, hence
 * mono caps at label size with the mark reduced to 14px.
 */
export function PoweredByFoundation({ tone = "light", size = 14, style, ...rest }) {
  const color = tone === "dark" ? "rgba(242,239,231,0.7)" : C.secondary;
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 8, ...style }}
      {...rest}
    >
      <span
        style={{
          fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color,
        }}
      >
        Powered by
      </span>
      <FoundationMark size={size} tone={tone} />
      <FoundationWordmark size={size * 0.82} color={tone === "dark" ? C.paper : INK} />
    </span>
  );
}

export default FoundationLogo;
